export const DEFAULT_INPUT = {
  currentCorpus: 2000000,
  currentAge: 35,
  returnBeforeWithdrawal: 12,
  withdrawalStartAge: 50,
  monthlyWithdrawal: 60000,
  yearlyWithdrawalIncrease: 5,
  returnDuringWithdrawal: 10
};

export const INPUT_LIMITS = {
  currentCorpus: {min: 100000, max: 100000000, step: 50000},
  currentAge: {min: 18, max: 100, step: 1},
  returnBeforeWithdrawal: {min: 1, max: 12, step: 0.5},
  withdrawalStartAge: {min: 18, max: 100, step: 1},
  monthlyWithdrawal: {min: 5000, max: 2000000, step: 5000},
  yearlyWithdrawalIncrease: {min: 0, max: 100, step: 0.5},
  returnDuringWithdrawal: {min: 1, max: 12, step: 0.5}
};

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const roundToStep = (value, step) => Math.round(value / step) * step;

const normalizeInput = (input) => ({
  currentCorpus: clamp(
    roundToStep(input.currentCorpus, INPUT_LIMITS.currentCorpus.step),
    INPUT_LIMITS.currentCorpus.min,
    INPUT_LIMITS.currentCorpus.max
  ),
  currentAge: clamp(
    Math.round(input.currentAge),
    INPUT_LIMITS.currentAge.min,
    INPUT_LIMITS.currentAge.max
  ),
  returnBeforeWithdrawal: clamp(
    roundToStep(input.returnBeforeWithdrawal, INPUT_LIMITS.returnBeforeWithdrawal.step),
    INPUT_LIMITS.returnBeforeWithdrawal.min,
    INPUT_LIMITS.returnBeforeWithdrawal.max
  ),
  withdrawalStartAge: clamp(
    Math.round(input.withdrawalStartAge),
    INPUT_LIMITS.withdrawalStartAge.min,
    INPUT_LIMITS.withdrawalStartAge.max
  ),
  monthlyWithdrawal: clamp(
    roundToStep(input.monthlyWithdrawal, INPUT_LIMITS.monthlyWithdrawal.step),
    INPUT_LIMITS.monthlyWithdrawal.min,
    INPUT_LIMITS.monthlyWithdrawal.max
  ),
  yearlyWithdrawalIncrease: clamp(
    roundToStep(input.yearlyWithdrawalIncrease, INPUT_LIMITS.yearlyWithdrawalIncrease.step),
    INPUT_LIMITS.yearlyWithdrawalIncrease.min,
    INPUT_LIMITS.yearlyWithdrawalIncrease.max
  ),
  returnDuringWithdrawal: clamp(
    roundToStep(input.returnDuringWithdrawal, INPUT_LIMITS.returnDuringWithdrawal.step),
    INPUT_LIMITS.returnDuringWithdrawal.min,
    INPUT_LIMITS.returnDuringWithdrawal.max
  )
});

export const calculateSwp = (input) => {
  const investmentData = normalizeInput(input);

  const currentCorpus = Number(investmentData.currentCorpus);
  const currentAge = Number(investmentData.currentAge);
  const returnPreWithdraw = Number(investmentData.returnBeforeWithdrawal);
  const withdrawalStartAge = Number(investmentData.withdrawalStartAge);
  const initialMonthlyWithdrawal = Number(investmentData.monthlyWithdrawal);
  const stepUp = Number(investmentData.yearlyWithdrawalIncrease);
  const returnDuringWithdraw = Number(investmentData.returnDuringWithdrawal);
  const maxAge = 100;

  const yearlyData = [];
  let corpus = currentCorpus;
  let monthlyWithdrawal = initialMonthlyWithdrawal;
  let corpusBeforeSwp = currentCorpus;
  let corpusUsed = 0;

  for (let age = currentAge; age <= maxAge; age++) {
    const isWithdrawalPhase = age >= withdrawalStartAge;
    let yearlyWithdrawal = 0;
    let corpusEnd = 0;
    const investedAmount = corpus;
    let gainAmount = 0;

    if (!isWithdrawalPhase) {
      corpusEnd = corpus * (1 + returnPreWithdraw / 100);
      gainAmount = corpusEnd - corpus;
    } else {
      if (age > withdrawalStartAge) {
        monthlyWithdrawal *= 1 + stepUp / 100;
      }
      yearlyWithdrawal = monthlyWithdrawal * 12;
      corpusEnd = corpus * (1 + returnDuringWithdraw / 100) - yearlyWithdrawal;
      gainAmount = corpus * (returnDuringWithdraw / 100);
    }

    if (age === withdrawalStartAge - 1) {
      corpusBeforeSwp = corpusEnd;
    }

    if (corpusEnd < 0 && isWithdrawalPhase) {
      break;
    }

    if (isWithdrawalPhase) {
      corpusUsed += yearlyWithdrawal;
    }

    yearlyData.push({
      year: age,
      monthly: isWithdrawalPhase ? Math.round(monthlyWithdrawal) : 0,
      yearly: Math.round(yearlyWithdrawal),
      yearLabel: `Age ${age}`,
      yearEndCorpus: Math.round(corpusEnd),
      Investment: Math.round(investedAmount),
      Gain: Math.round(gainAmount),
      totalValue: Math.round(corpusEnd)
    });

    corpus = corpusEnd;
  }

  const validAges = yearlyData.map((item) => item.year).map(Number).sort((a, b) => a - b);
  let sustainTillAge = currentAge - 1;
  let finalCorpus = 0;

  for (const age of validAges) {
    const yearData = yearlyData.find((item) => item.year === age);
    if (yearData && yearData.yearEndCorpus >= 0) {
      sustainTillAge = age;
      finalCorpus = yearData.yearEndCorpus;
    } else {
      break;
    }
  }

  return {
    returnData: yearlyData,
    sustainTillAge,
    finalCorpus: Math.round(finalCorpus),
    corpusBeforeSwp: Math.round(corpusBeforeSwp),
    corpusUsed: Math.round(corpusUsed)
  };
};

export const formatCurrency = (value) =>
  `₹${Math.round(value).toLocaleString("en-IN")}`;

export const formatCompactCurrency = (value) => {
  const absValue = Math.abs(value);

  if (absValue >= 10000000) {
    return `₹${(value / 10000000).toFixed(1)}Cr`;
  }

  if (absValue >= 100000) {
    return `₹${(value / 100000).toFixed(1)}L`;
  }

  if (absValue >= 1000) {
    return `₹${(value / 1000).toFixed(0)}k`;
  }

  return formatCurrency(value);
};

export const snapToStep = (value, step) => roundToStep(value, step);
